import {
  ASSET_VERIFIED,
  buildGroupedRows,
  isGroupHeaderRow,
  resolveGroupedRowsLoadMoreIndex,
  type AssetVerified,
  type GroupHeaderRowBase,
} from '@terra/ui-design-system';
import type {
  AssetPreviewDto,
  AssetsSearchGroup,
  AssetsSearchGroupBy,
  AssetsSearchOrderBy,
  AssetsSearchOrderByField,
} from '@terra/shared-contracts-assets';
import {
  ASSET_TYPE,
  type AssetType,
} from '../../../store/tab/assetsTabScopedFilterStore';
import {
  ASSETS_TABLE_GROUP_BY,
  type AssetsTableGroupBy,
  type AssetsTableSort,
} from '../../../store/tab/assetsTableViewTabScopedStore';

export const ASSETS_TABLE_KIND = {
  ENDPOINTS: 'endpoints',
  WEB_PAGES: 'webpages',
  UNVERIFIED: 'unverified',
} as const;

export type AssetsTableKind =
  (typeof ASSETS_TABLE_KIND)[keyof typeof ASSETS_TABLE_KIND];

export const ASSETS_TABLE_GROUP_HEADER_ID_PREFIX = '__assets_group__:';

export type AssetsTableGroupHeaderRow = GroupHeaderRowBase;

export type AssetsTableRow = AssetPreviewDto | AssetsTableGroupHeaderRow;

const UNVERIFIED_SORTABLE_FIELDS: readonly AssetsSearchOrderByField[] = [
  'createdAt',
  'url',
];

type UnverifiedApiType = Extract<
  AssetPreviewDto,
  { assetType: 'unverifiedEndpoint' }
>['apiType'];

const UNVERIFIED_API_TYPE_LABEL: Record<UnverifiedApiType, string | null> = {
  UNKNOWN: null,
  REST: 'REST',
  GRAPHQL: 'GraphQL',
  SOAP: 'SOAP',
  WEBSOCKET: 'WebSocket',
  GRPC: 'gRPC',
};

export const getUnverifiedApiTypeLabel = (
  apiType: UnverifiedApiType,
): string | null => UNVERIFIED_API_TYPE_LABEL[apiType];

export const isAssetsTableGroupHeaderRow = (
  row: AssetsTableRow | null | undefined,
): row is AssetsTableGroupHeaderRow => isGroupHeaderRow(row);

export const resolveAssetsTableKind = ({
  typeFilter,
  verified,
}: {
  typeFilter: AssetType;
  verified: AssetVerified;
}): AssetsTableKind => {
  if (typeFilter === ASSET_TYPE.WEB_PAGES) {
    return ASSETS_TABLE_KIND.WEB_PAGES;
  }

  if (typeFilter === ASSET_TYPE.ENDPOINTS && verified === ASSET_VERIFIED.NO) {
    return ASSETS_TABLE_KIND.UNVERIFIED;
  }

  return ASSETS_TABLE_KIND.ENDPOINTS;
};

const GROUP_BY_OPTIONS_BY_KIND: Record<
  AssetsTableKind,
  readonly AssetsSearchGroupBy[]
> = {
  [ASSETS_TABLE_KIND.ENDPOINTS]: ['domain', 'method'],
  [ASSETS_TABLE_KIND.WEB_PAGES]: ['domain'],
  [ASSETS_TABLE_KIND.UNVERIFIED]: [],
};

export const getAssetsTableGroupByOptions = (
  kind: AssetsTableKind,
): readonly AssetsSearchGroupBy[] => GROUP_BY_OPTIONS_BY_KIND[kind];

export const resolveEffectiveAssetsGroupBy = ({
  groupBy,
  kind,
}: {
  groupBy: AssetsTableGroupBy;
  kind: AssetsTableKind;
}): AssetsSearchGroupBy | undefined => {
  const options = getAssetsTableGroupByOptions(kind);

  if (groupBy === ASSETS_TABLE_GROUP_BY.NONE || options.length === 0) {
    return undefined;
  }

  return options.includes(groupBy) ? groupBy : options[0];
};

export const isAssetsTableFieldSortable = ({
  kind,
  field,
}: {
  kind: AssetsTableKind;
  field: AssetsSearchOrderByField;
}): boolean =>
  kind !== ASSETS_TABLE_KIND.UNVERIFIED ||
  UNVERIFIED_SORTABLE_FIELDS.includes(field);

export const resolveAssetsTableOrderBy = ({
  sort,
  kind,
}: {
  sort: AssetsTableSort | null;
  kind: AssetsTableKind;
}): AssetsSearchOrderBy[] | undefined => {
  if (!sort) {
    return undefined;
  }

  return isAssetsTableFieldSortable({ kind, field: sort.field })
    ? [sort]
    : undefined;
};

export const getWebpageUrlHost = (url: string): string => {
  const [, afterScheme] = url.split('://');
  const [host] = (afterScheme ?? url).split('/');

  return host ?? '';
};

export const getAssetDomain = (asset: AssetPreviewDto): string =>
  asset.assetType === 'webpage' ? getWebpageUrlHost(asset.url) : asset.host;

export const getAssetGroupKey = ({
  asset,
  groupBy,
}: {
  asset: AssetPreviewDto;
  groupBy: AssetsSearchGroupBy;
}): string => {
  if (groupBy === 'method' && asset.assetType !== 'webpage') {
    return asset.request.method;
  }

  return getAssetDomain(asset);
};

export const buildAssetsTableRows = ({
  assets,
  groupBy,
  serverGroups,
  collapsedGroupKeys,
  emptyGroupLabel,
}: {
  assets: readonly AssetPreviewDto[];
  groupBy: AssetsSearchGroupBy | undefined;
  serverGroups: readonly AssetsSearchGroup[] | undefined;
  collapsedGroupKeys: ReadonlySet<string>;
  emptyGroupLabel: string;
}): AssetsTableRow[] => {
  if (!groupBy) {
    return [...assets];
  }

  const serverCountByKey = new Map(
    (serverGroups ?? []).map((group) => [group.key, group.count]),
  );

  return buildGroupedRows<AssetPreviewDto, AssetsTableGroupHeaderRow>({
    items: assets,
    getItemId: (asset) => asset.id,
    getGroupKey: (asset) => getAssetGroupKey({ asset, groupBy }),
    getGroupLabel: (groupKey) => groupKey || emptyGroupLabel,
    compareGroupKeys: serverGroups
      ? undefined
      : (left, right) => left.localeCompare(right),
    collapsedGroupKeys,
    headerIdPrefix: ASSETS_TABLE_GROUP_HEADER_ID_PREFIX,
    knownGroupKeys: (serverGroups ?? []).map((group) => group.key),
    decorateHeader: (header) => ({
      ...header,
      count: serverCountByKey.get(header.groupKey) ?? header.count,
    }),
  });
};

export const resolveAssetsTableLoadMoreRowIndex = ({
  rows,
  isGroupedOnServer,
  collapsedGroupKeys,
}: {
  rows: readonly AssetsTableRow[];
  isGroupedOnServer: boolean;
  collapsedGroupKeys: ReadonlySet<string>;
}): number | null => {
  if (!isGroupedOnServer) {
    return rows.length - 1;
  }

  return resolveGroupedRowsLoadMoreIndex({
    rows,
    collapsedGroupKeys,
    hasUnseenGroups: false,
  });
};

export const getNewAssetIds = ({
  assets,
  lastVisitTimestamp,
}: {
  assets: readonly AssetPreviewDto[];
  lastVisitTimestamp: Date | null | undefined;
}): ReadonlySet<string> => {
  if (!lastVisitTimestamp) {
    return new Set();
  }

  return new Set(
    assets
      .filter((asset) => new Date(asset.createdAt) > lastVisitTimestamp)
      .map((asset) => asset.id),
  );
};
